import React, { useContext, useState } from 'react';
import { AuthContext } from '../../context/logeduser';
import { StyledInput, Label } from '../StyledComponents/StyledComponent';
import { axiosInstance } from '../../api';
import Evt from '../../assets/admin_login.png';
import { useAlert } from '../../context/AlertContext';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  IconButton,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import LoginToggle from './LoginToggle';
import AdminResetPassword from './AdminResetPassword';
import { useCompany } from '../../context/CompanyContext';
import { clearAuthData } from '../../utils/authStorage';

function SignInForm() {
  const { showSuccess, showError } = useAlert();
  const [state, setState] = useState({
    user_name: '',
    password: '',
  });
  const [error] = useState(null);
  const { login } = useContext(AuthContext);
  const company = useCompany();
  const [resetData, setResetData] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleChange = (evt) => {
    const value = evt.target.value;
    setState({
      ...state,
      [evt.target.name]: value,
    });
  };

  const handleOnSubmit = async (evt) => {
    evt.preventDefault();
    const { user_name, password } = state;

    try {
      clearAuthData();
      const response = await axiosInstance.post(
        'api/admin/login/',
        { user_name, password },
      );

      if (response.data.success === true) {
        if (response.data.must_reset_password) {
          setResetData({ adminId: response.data.admin?.id });
          return;
        }
        login(response.data);
        showSuccess("Login successful! Redirecting...", {
          title: "Success",
          position: "top-center"
        });
      } else {
        showError(
          response.data.message || "Login failed. Please check your credentials.",
          {
            title: "Login Failed",
            position: "top-center"
          }
        );
      }
    } catch (err) {
      showError(
        err.response?.data?.message || "Failed to login. Please try again.",
        {
          title: "Error",
          autoHideDuration: 5000
        }
      );
      console.error('Error during API request:', err.response || err.message);
    }

    setState({
      user_name: '',
      password: '',
    });
  };

  if (resetData) {
    return (
      <AdminResetPassword
        adminId={resetData.adminId}
        onComplete={() => {
          setResetData(null);
          showSuccess('Password updated! Please log in with your new credentials.', {
            title: 'Success', position: 'top-center',
          });
        }}
      />
    );
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2
    }}>
      <Paper elevation={12} sx={{
        width: '100%',
        maxWidth: 1000,
        display: 'flex',
        borderRadius: 6,
        overflow: 'hidden',
        minHeight: { xs: 'auto', md: 600 }
      }}>
        <Grid container>
          {/* Left: Form Section */}
          <Grid item xs={12} md={6} sx={{
            p: { xs: 4, md: 6 },
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center'
          }}>
            <Box mb={4} sx={{ textAlign: 'center', minHeight: 100 }}>
              {!company.loading && (
                <>
                  {company.logo && <img src={company.logo} alt={company.name || 'Company logo'} onError={(event) => { event.currentTarget.style.display = 'none'; }} style={{ height: 60, marginBottom: 24 }} />}
                  {company.name && (
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b', mt: 1 }}>
                      {company.name}
                    </Typography>
                  )}
                </>
              )}
            </Box>

            <LoginToggle />

            <Box component="form" onSubmit={handleOnSubmit}>
              <Box mb={3}>
                <Label>Admin Username</Label>
                <StyledInput
                  fullWidth
                  type="text"
                  placeholder="Enter username"
                  name="user_name"
                  value={state.user_name}
                  onChange={handleChange}
                />
              </Box>

              <Box mb={4}>
                <Label>Password</Label>
                <Box sx={{ position: 'relative' }}>
                  <StyledInput
                    fullWidth
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter password"
                    name="password"
                    value={state.password}
                    onChange={handleChange}
                  />
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    size="small"
                    sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}
                  >
                    {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                  </IconButton>
                </Box>
              </Box>

              <Button
                type="submit"
                variant="contained"
                fullWidth
                sx={{
                  py: 1.5,
                  backgroundColor: '#218838',
                  fontSize: '1rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#1e7e34',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 20px rgba(33, 136, 56, 0.3)'
                  },
                  transition: 'all 0.3s ease',
                  fontWeight: 'bold',
                  borderRadius: 3,
                }}
              >
                Sign In
              </Button>

              {error && (
                <Typography color="error" mt={2} textAlign="center" variant="body2">
                  {error}
                </Typography>
              )}
            </Box>
          </Grid>

          {/* Right: Illustration */}
          <Grid item md={6} sx={{
            display: { xs: 'none', md: 'flex' },
            background: 'linear-gradient(45deg, #f0f4f8 0%, #d9e2ec 100%)',
            alignItems: 'center',
            justifyContent: 'center',
            p: 4
          }}>
            <Box sx={{
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              animation: 'float 6s ease-in-out infinite'
            }}>
              <img src={Evt} alt="Admin Login Visual" style={{ maxWidth: '90%', height: 'auto' }} />
            </Box>
            <style>
              {`
                @keyframes float {
                  0% { transform: translateY(0px); }
                  50% { transform: translateY(-20px); }
                  100% { transform: translateY(0px); }
                }
              `}
            </style>
          </Grid>
        </Grid>
      </Paper>
    </Box>
  );
}

export default SignInForm;
